import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
    Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import { Bucket, TimeseriesResponse } from '../../services/executionStatsService';
import { useChartChrome } from '../CaseWorkflow/caseWorkflowPalette';
import {
    useBucketColours, BUCKET_LABELS, BUCKET_ORDER, NOT_TRACKED, count, failureRate, percent, trackedFromNote
} from './executionStatsVocabulary';
import { periodLabel } from './executionStatsWindows';
import { dsVar } from '../../theme/tokens';

interface StatusTrendChartProps {
    data: TimeseriesResponse;
    height?: number;
}

/**
 * Runs per period, stacked by outcome.
 *
 * Stacked because the outcomes add up to the period's total. Success is grey
 * and failures carry the critical colour, so a bad day stands out without
 * reading the legend. The failure rate is in the tooltip rather than a line on
 * a second axis: two y-scales on one chart invite comparing things that don't
 * share a unit.
 *
 * The axis names the time zone: periods are the viewer's calendar days, and a
 * silent zone moves runs across day boundaries.
 */
const StatusTrendChart: React.FC<StatusTrendChartProps> = ({ data, height = 260 }) => {
    const chrome = useChartChrome();
    const bucketColours = useBucketColours();
    // Untracked buckets are null throughout, so they get no series at all
    // rather than a legend entry that never has a bar. Pending review can be
    // tracked from a period on, so a bucket counted in any period gets one.
    const series = useMemo(
        () => BUCKET_ORDER.filter((bucket) =>
            data.totals[bucket] !== null || data.points.some((point) => point[bucket] !== null)),
        [data.totals, data.points]
    );
    const reviewsFrom = series.includes('pendingReview') && data.totals.pendingReview === null
        ? trackedFromNote(data.runtimeStatusSince)
        : null;

    const chartData = useMemo(
        () => data.points.map((point) => ({
            ...point,
            label: periodLabel(point.period, data.granularity)
        })),
        [data.points, data.granularity]
    );

    if (!data.totals.total) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                No runs started in this range.
            </Typography>
        );
    }

    return (
        <Box>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                    <CartesianGrid stroke={chrome.grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: chrome.axisText, fontSize: 11 }} stroke={chrome.grid} tickLine={false} />
                    <YAxis
                        allowDecimals={false}
                        tick={{ fill: chrome.axisText, fontSize: 11 }}
                        stroke={chrome.grid}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip
                        cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                        content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const point = payload[0].payload;
                            return (
                                <Box
                                    sx={{
                                        backgroundColor: chrome.surface, border: `1px solid ${chrome.grid}`, borderRadius: '8px',
                                        p: 1.25, fontSize: 12, color: dsVar('fg-soft')
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: dsVar('fg'), mb: 0.5 }}>
                                        {point.label} · {count(point.total)} {point.total === 1 ? 'run' : 'runs'}
                                    </Typography>
                                    {[...series].reverse().map((bucket: Bucket) => (
                                        <Box key={bucket} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: bucketColours[bucket] }} />
                                            <span>{BUCKET_LABELS[bucket]}</span>
                                            <span style={{ marginLeft: 'auto', paddingLeft: 12 }}>
                                                {point[bucket] === null ? NOT_TRACKED : count(point[bucket])}
                                            </span>
                                        </Box>
                                    ))}
                                    <Typography sx={{ fontSize: 11, color: dsVar('fg-2'), mt: 0.5 }}>
                                        Failure rate {percent(failureRate(point))}
                                    </Typography>
                                </Box>
                            );
                        }}
                    />
                    <Legend
                        wrapperStyle={{ fontSize: 11, color: chrome.axisText }}
                        formatter={(key) => BUCKET_LABELS[key as Bucket]}
                    />
                    {series.map((bucket) => (
                        <Bar
                            key={bucket}
                            dataKey={bucket}
                            stackId="runs"
                            fill={bucketColours[bucket]}
                            // A surface-coloured edge keeps neighbouring segments apart.
                            stroke={chrome.surface}
                            strokeWidth={1}
                            maxBarSize={40}
                            isAnimationActive={false}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.5, textAlign: 'right' }}>
                By the {data.granularity} each run started · {data.timezone}
                {reviewsFrom && ` · ${BUCKET_LABELS.pendingReview} ${reviewsFrom.toLowerCase()}`}
            </Typography>
        </Box>
    );
};

export default StatusTrendChart;
